import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Popover,
  Tooltip,
  Tabs,
  Tab,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import { handleTitleCaseChange } from '../utils/textUtils';
import { ValidationRules, validateForm, hasErrors, formatErrors } from '../utils/validation';
import { createTooltipProps } from '../utils/fieldTooltips';
import MissingSelectOption from '../components/MissingSelectOption';

// ValidatedFormControl defined outside to prevent recreation on every render
const ValidatedFormControl = React.memo(
  ({
    name,
    label,
    required,
    children,
    tooltipDescription,
    validationErrors = {},
    formData = {},
    handleChange,
    ...props
  }) => {
    const hasError = validationErrors[name];
    const popover = hasError ? (
      <Popover id={`popover-${name}`}>
        <Popover.Header as="h3">Validation Error</Popover.Header>
        <Popover.Body>{validationErrors[name]}</Popover.Body>
      </Popover>
    ) : null;

    const tooltip = tooltipDescription ? (
      <Tooltip id={`tooltip-${name}`}>{tooltipDescription}</Tooltip>
    ) : null;

    const formControl = children || (
      <Form.Control
        {...props}
        name={name}
        value={formData[name] || ''}
        onChange={handleChange}
        isInvalid={hasError}
      />
    );

    const wrappedControl = hasError ? (
      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popover}>
        {formControl}
      </OverlayTrigger>
    ) : tooltipDescription ? (
      <OverlayTrigger placement="top" overlay={tooltip}>
        {formControl}
      </OverlayTrigger>
    ) : (
      formControl
    );

    return (
      <Form.Group className="mb-3">
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        {wrappedControl}
      </Form.Group>
    );
  }
);

function StaffModal({ staff, onClose, onSave }) {
  const [modalError, setModalError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [depots, setDepots] = useState([]);
  const [formData, setFormData] = useState({
    short_name: '',
    first_name: '',
    surname: '',
    employee_no: '',
    hiredesk: false,
    driver: false,
    operator: false,
    workshop: false,
    sales: false,
    purchase: false,
    cost_per_hour: undefined,
    recharge_per_hour: undefined,
    depot: '',
    telephone: '',
    mobile: '',
    email: '',
    job_title: '',
    date_of_birth: '',
    ni_number: '',
    dl_number: '',
    start_date: '',
    holidays: '',
    work_hours: '',
  });

  useEffect(() => {
    if (staff) {
      // Convert 0 values to undefined for financial fields
      const cleanedData = { ...staff };
      const financialFields = ['cost_per_hour', 'recharge_per_hour'];
      financialFields.forEach((field) => {
        if (
          cleanedData[field] === 0 ||
          cleanedData[field] === '0' ||
          cleanedData[field] === '0.00'
        ) {
          cleanedData[field] = undefined;
        }
      });
      setFormData(cleanedData);
    }
    fetchDepots();
  }, [staff]);

  const fetchDepots = async () => {
    try {
      const response = await axios.get('/api/depots?limit=1000');
      setDepots(response.data.depots || []);
    } catch (error) {
      console.error('Error fetching depots:', error);
    }
  };

  const titleCaseFields = [
    'first_name',
    'surname',
    'job_title',
    'adr_line1',
    'adr_line2',
    'adr_line3',
    'adr_town',
    'adr_county',
  ];

  const handleChange = (e) => {
    handleTitleCaseChange(e, setFormData, formData, titleCaseFields);

    // Clear validation errors for this field when user starts typing
    if (validationErrors[e.target.name]) {
      const newErrors = { ...validationErrors };
      delete newErrors[e.target.name];
      setValidationErrors(newErrors);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setModalError('');

    // Comprehensive validation using validation rules
    const errors = validateForm(formData, ValidationRules.staff);
    setValidationErrors(errors);

    if (hasErrors(errors)) {
      setModalError(formatErrors(errors));
      return;
    }

    // Additional validation: At least one role must be specified
    const roles = ['hiredesk', 'driver', 'operator', 'workshop', 'sales', 'purchase'];
    const hasRole = roles.some((role) => formData[role]);
    if (!hasRole) {
      setModalError(
        'At least one role must be specified (Hire Desk, Driver, Operator, Workshop, Sales, or Purchasing)'
      );
      return;
    }

    onSave(formData);
  };

  const handleCopy = () => {
    const copiedData = { ...formData };
    delete copiedData.id;
    copiedData.employee_no = copiedData.employee_no + '_copy';
    copiedData.first_name = copiedData.first_name + ' (Copy)';
    setFormData(copiedData);
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>{staff ? 'Edit Staff Member' : 'Add New Staff Member'}</Modal.Title>
        {staff && (
          <Button variant="secondary" onClick={handleCopy} className="ms-2">
            Copy Row
          </Button>
        )}
      </Modal.Header>

      {modalError && (
        <div className="alert alert-danger mx-3 mt-3 mb-0" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {modalError}
        </div>
      )}

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Tabs defaultActiveKey="personal" className="mb-3">
            <Tab eventKey="personal" title="Personal">
              <Row>
                <Col md={6}>
                  <ValidatedFormControl
                    name="short_name"
                    label="Short Name"
                    type="text"
                    tooltipDescription="Short name or nickname for the staff member"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={6}>
                  <ValidatedFormControl
                    name="employee_no"
                    label="Employee No"
                    type="text"
                    tooltipDescription="Unique employee identification number"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <ValidatedFormControl
                    name="first_name"
                    label="First Name"
                    type="text"
                    tooltipDescription="Staff member's first name"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={6}>
                  <ValidatedFormControl
                    name="surname"
                    label="Surname"
                    required={true}
                    type="text"
                    tooltipDescription="Staff member's surname or last name"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Job Title</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('job_title')}>
                          Staff member's job title or position
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="job_title"
                        value={formData.job_title}
                        onChange={handleChange}
                        {...createTooltipProps('job_title')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Birth</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('date_of_birth')}>
                          Staff member's date of birth
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="date"
                        name="date_of_birth"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        {...createTooltipProps('date_of_birth')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>NI Number</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('ni_number')}>
                          National Insurance number
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="ni_number"
                        value={formData.ni_number}
                        onChange={handleChange}
                        {...createTooltipProps('ni_number')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>DL Number</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('dl_number')}>
                          Driving license number
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="dl_number"
                        value={formData.dl_number}
                        onChange={handleChange}
                        {...createTooltipProps('dl_number')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="employment" title="Employment">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('start_date')}>
                          Employment start date
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        {...createTooltipProps('start_date')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Depot</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('depot')}>
                          Used for availability checking, delivery planning, and equipment tracking
                        </Tooltip>
                      }
                    >
                      <Form.Select
                        name="depot"
                        value={formData.depot}
                        onChange={handleChange}
                        {...createTooltipProps('depot')}
                      >
                        <option value="">Select a depot...</option>
                        <MissingSelectOption
                          value={formData.depot}
                          options={depots}
                          getOptionValue={(depot) => depot.short_code}
                        />
                        {depots.map((depot) => (
                          <option key={depot.id} value={depot.short_code}>
                            {depot.depot_name}
                          </option>
                        ))}
                      </Form.Select>
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="contact" title="Contact &amp; Permissions">
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telephone</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('telephone')}>
                          Staff member's telephone number
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        {...createTooltipProps('telephone')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mobile</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('mobile')}>
                          Staff member's mobile number
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        {...createTooltipProps('mobile')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('email')}>
                          Staff member's email address
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        {...createTooltipProps('email')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <ValidatedFormControl
                    name="cost_per_hour"
                    label="Cost Per Hour"
                    type="number"
                    step="0.01"
                    tooltipDescription="Hourly cost rate for the staff member"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={4}>
                  <ValidatedFormControl
                    name="recharge_per_hour"
                    label="Recharge Per Hour"
                    type="number"
                    step="0.01"
                    tooltipDescription="Hourly recharge rate for billing"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Hours</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('work_hours')}>
                          Standard work hours per period
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="number"
                        name="work_hours"
                        value={formData.work_hours}
                        onChange={handleChange}
                        step="0.1"
                        {...createTooltipProps('work_hours')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Holidays</Form.Label>
                    <Form.Control
                      type="number"
                      name="holidays"
                      value={formData.holidays}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="mb-3">
                <h5>Permissions</h5>
                <Row>
                  {['hiredesk', 'driver', 'operator', 'workshop', 'sales', 'purchase'].map(
                    (permission) => (
                      <Col md={4} key={permission}>
                        <Form.Group className="mb-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps(permission)}>
                                Grants {permission} permissions to the staff member
                              </Tooltip>
                            }
                          >
                            <Form.Check
                              type="checkbox"
                              name={permission}
                              label={permission.charAt(0).toUpperCase() + permission.slice(1)}
                              checked={formData[permission]}
                              onChange={handleChange}
                              className="mb-2"
                              {...createTooltipProps(permission)}
                            />
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>
                    )
                  )}
                </Row>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {staff ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function Staff() {
  const [staff, setStaff] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const fetchDepots = async () => {
    try {
      const response = await axios.get('/api/depots?limit=1000');
      setDepots(response.data.depots || []);
    } catch (err) {
      console.error('Error fetching depots:', err);
    }
  };

  const fetchStaff = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/staff', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });
      setStaff(response.data.staff);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch staff members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
    fetchDepots();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStaff(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchStaff(newPage, searchTerm);
  };

  const handleAddStaff = () => {
    setEditingStaff(null);
    setShowModal(true);
  };

  const handleEditStaff = (staffMember) => {
    setEditingStaff(staffMember);
    setShowModal(true);
  };

  const handleSaveStaff = async (staffData) => {
    try {
      if (editingStaff) {
        await axios.put(`/api/staff/${editingStaff.id}`, staffData);
        setSuccess('Staff member updated successfully');
      } else {
        await axios.post('/api/staff', staffData);
        setSuccess('Staff member created successfully');
      }
      setShowModal(false);
      fetchStaff(pagination.page, searchTerm);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save staff member');
    }
  };

  const handleDeleteStaff = async (id) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await axios.delete(`/api/staff/${id}`);
        setSuccess('Staff member deleted successfully');
        fetchStaff(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete staff member');
      }
    }
  };

  const handleDeleteAllStaff = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL staff members? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your staff records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/staff');
          setSuccess(
            `All staff members deleted successfully (${response.data.deletedCount} records)`
          );
          fetchStaff(1, '');
        } catch (error) {
          setError('Failed to delete all staff members');
        }
      }
    }
  };

  const handleDepotChange = async (staffId, newDepot) => {
    try {
      await axios.put(`/api/staff/${staffId}`, { depot: newDepot || null });
      setStaff((prev) => prev.map((s) => (s.id === staffId ? { ...s, depot: newDepot || '' } : s)));
      setSuccess('Depot updated successfully');
    } catch (err) {
      console.error('Error updating depot:', err);
      setError('Failed to update depot');
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-people-fill me-3 text-primary" style={{ fontSize: '2rem' }}></i>
                <div>
                  <h1 className="mb-0">Staff Members</h1>
                  <p className="text-muted mb-0">Manage your staff members database</p>
                </div>
              </div>
            </div>
            <div>
              <Button variant="primary" className="me-2" onClick={handleAddStaff}>
                Add Staff Member
              </Button>
              <Button variant="danger" onClick={handleDeleteAllStaff}>
                Delete All
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search staff members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>
          {loading ? (
            <div className="text-center p-3">Loading staff members...</div>
          ) : staff.length === 0 ? (
            <div className="text-center p-3">No staff members found.</div>
          ) : (
            <>
              <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
                <Table striped bordered hover className="mb-3" style={{ marginLeft: 0 }}>
                  <thead>
                    <tr>
                      <th>Short Name</th>
                      <th>Full Name</th>
                      <th>Job Title</th>
                      <th>Depot</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Permissions</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map((staffMember) => {
                      // Debug: Log the permissions to see what we're working with
                      return (
                        <tr key={staffMember.id}>
                          <td className="staff-name">{staffMember.short_name}</td>
                          <td className="staff-name">
                            {`${staffMember.first_name || ''} ${staffMember.surname || ''}`.trim()}
                          </td>
                          <td>{staffMember.job_title}</td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={staffMember.depot || ''}
                              onChange={(e) => handleDepotChange(staffMember.id, e.target.value)}
                              style={{ minWidth: '100px', padding: '2px 4px', fontSize: '0.8rem' }}
                            >
                              <option value="">Select depot...</option>
                              <MissingSelectOption
                                value={staffMember.depot}
                                options={depots}
                                getOptionValue={(depot) => depot.short_code}
                              />
                              {depots.map((depot) => (
                                <option key={depot.id} value={depot.short_code}>
                                  {depot.depot_name}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>{staffMember.email}</td>
                          <td>{staffMember.mobile}</td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              {['hiredesk', 'driver', 'operator', 'workshop', 'sales', 'purchase']
                                .filter((permission) => {
                                  // Only include permissions that are truthy and not 0
                                  const value = staffMember[permission];
                                  return (
                                    value &&
                                    value !== 0 &&
                                    value !== '0' &&
                                    value !== false &&
                                    value !== 'false'
                                  );
                                })
                                .map((permission) => (
                                  <Badge
                                    key={permission}
                                    bg="primary"
                                    className="text-uppercase"
                                    style={{ fontSize: '0.6rem' }}
                                  >
                                    {permission}
                                  </Badge>
                                ))}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditStaff(staffMember)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteStaff(staffMember.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <StaffModal
          staff={editingStaff}
          onClose={() => setShowModal(false)}
          onSave={handleSaveStaff}
        />
      )}
    </Container>
  );
}

export default Staff;
